import { useEffect } from "react";
import { useParams, Link } from "react-router-dom";
import { ArrowLeft, ArrowRight, Check, Crown, Users, ShieldCheck, Sparkles } from "lucide-react";
import { Reveal } from "@/components/Reveal";
import UpgradeDialog from "@/components/UpgradeDialog";
import { getPlanBySlug, planSection, planSlug, ALL_PLANS } from "@/data/site";

const WHO = {
  "Free (Starter)": "Anyone getting started who wants to try Warranty_Vault pro for free.",
  "Pro Lifetime": "A single person who wants everything unlocked — forever, with one payment.",
  "Family Lifetime": "Households who want to share one vault across up to five accounts.",
  "Family Jumbo": "Families where every member also wants their own private Pro vault.",
  "Signature Edition": "Individuals and groups who want their own branding on the experience.",
  "Signature Jumbo": "Larger branded groups where each account also gets its own Pro vault.",
  "Business": "Small teams managing receipts, warranties and tax documents together.",
  "Business Jumbo": "Teams who want business tools plus an individual Pro vault per member.",
  "Elite Edition": "Businesses that need a full white-label solution and custom domain.",
  "Elite Jumbo": "Businesses that want maximum control, automation and per-member vaults.",
};

export default function PlanDetail() {
  const { slug } = useParams();
  const plan = getPlanBySlug(slug);

  useEffect(() => {
    document.title = plan ? `${plan.tier} — Warranty_Vault pro` : "Plan — Warranty_Vault pro";
  }, [plan]);

  if (!plan) {
    return (
      <main className="min-h-screen pt-28" data-testid="plan-not-found">
        <div className="mx-auto max-w-2xl px-5 py-16 text-center sm:px-8">
          <h1 className="font-display text-3xl font-bold">Plan not found</h1>
          <Link to="/pricing" className="mt-6 inline-flex items-center gap-2 rounded-full bg-primary px-6 py-3 text-sm font-semibold text-primary-foreground">
            Back to pricing <ArrowRight className="h-4 w-4" />
          </Link>
        </div>
      </main>
    );
  }

  const section = planSection(plan);
  const isPaid = plan.price !== "Free";
  const related = ALL_PLANS.filter((p) => planSection(p) === section && p.tier !== plan.tier);

  return (
    <main className="relative min-h-screen pt-28" data-testid={`plan-detail-${slug}`}>
      <div className="vault-grid-bg grain absolute inset-x-0 top-0 h-96" />
      <div className="relative mx-auto max-w-5xl px-5 pb-10 sm:px-8">
        <Link to="/pricing" data-testid="plan-back" className="inline-flex items-center gap-2 text-sm font-medium text-muted-foreground transition-colors hover:text-gold">
          <ArrowLeft className="h-4 w-4" /> Back to all plans
        </Link>

        <Reveal className="mt-6">
          <p className="eyebrow inline-flex items-center gap-1.5">
            {isPaid ? <Crown className="h-3.5 w-3.5" /> : <Sparkles className="h-3.5 w-3.5" />} {plan.badge} · {section === "business" ? "Business" : "Individual"}
          </p>
          <h1 className="mt-3 font-display text-4xl font-extrabold tracking-tight sm:text-5xl">{plan.tier}</h1>
          <div className="mt-4 flex items-baseline gap-3">
            <span className="font-display text-5xl font-extrabold text-gold">{plan.price}</span>
            <span className="text-muted-foreground">· {plan.meta}</span>
          </div>
          <p className="mt-4 max-w-2xl text-lg text-muted-foreground">{plan.blurb}</p>
        </Reveal>

        <div className="mt-10 grid gap-6 lg:grid-cols-[1.4fr_1fr]">
          <Reveal className="rounded-3xl border border-border bg-card/50 p-7">
            <h2 className="font-display text-xl font-semibold">What's included</h2>
            <ul className="mt-5 space-y-3.5">
              {plan.features.map((f) => (
                <li key={f} className="flex items-start gap-3 text-sm">
                  <span className="mt-0.5 grid h-5 w-5 shrink-0 place-items-center rounded-full bg-sage/15 text-sage"><Check className="h-3.5 w-3.5" /></span>
                  <span className="text-foreground/90">{f}</span>
                </li>
              ))}
            </ul>
          </Reveal>

          <Reveal delay={0.08} className="flex flex-col gap-5">
            <div className="rounded-3xl border border-border bg-card/50 p-7">
              <h2 className="font-display text-xl font-semibold">At a glance</h2>
              <dl className="mt-5 space-y-4 text-sm">
                <div className="flex items-center gap-3">
                  <Users className="h-4 w-4 text-gold" />
                  <dt className="text-muted-foreground">Accounts</dt>
                  <dd className="ml-auto font-semibold capitalize">{plan.meta}</dd>
                </div>
                <div className="flex items-center gap-3">
                  <ShieldCheck className="h-4 w-4 text-gold" />
                  <dt className="text-muted-foreground">Billing</dt>
                  <dd className="ml-auto font-semibold">{isPaid ? "One-time · lifetime" : "Free forever"}</dd>
                </div>
                <div className="flex items-start gap-3">
                  <Sparkles className="mt-0.5 h-4 w-4 text-gold" />
                  <dt className="text-muted-foreground">Ideal for</dt>
                  <dd className="ml-auto max-w-[62%] text-right font-medium">{WHO[plan.tier]}</dd>
                </div>
              </dl>
            </div>

            <div className="rounded-3xl border border-border bg-card/50 p-7">
              {plan.buyLink ? (
                <>
                  <p className="mb-3 text-sm text-muted-foreground">Purchases are tied to your account and unlock instantly in the app.</p>
                  <UpgradeDialog plan={plan} />
                </>
              ) : (
                <p className="text-sm text-muted-foreground">This plan is included automatically when you sign up.</p>
              )}
            </div>
          </Reveal>
        </div>

        <Reveal className="mt-12">
          <h2 className="font-display text-lg font-semibold">Other {section === "business" ? "business" : "individual"} plans</h2>
          <div className="mt-4 flex flex-wrap gap-3">
            {related.map((p) => (
              <Link
                key={p.tier}
                to={`/plans/${planSlug(p)}`}
                data-testid={`related-${planSlug(p)}`}
                className="inline-flex items-center gap-2 rounded-full border border-border bg-card/40 px-4 py-2.5 text-sm font-medium transition-colors hover:border-gold/40 hover:text-gold"
              >
                {p.tier} <span className="text-muted-foreground">· {p.price}</span>
              </Link>
            ))}
          </div>
        </Reveal>
      </div>
    </main>
  );
}
